import React from 'react';
import { Link } from 'react-router-dom';
import { Stack, Typography, Button, Chip, Divider, Theme, CircularProgress, SxProps, Box } from '@mui/material';
import { ContentCopyOutlined, EditOutlined, RemoveRedEyeOutlined } from '@mui/icons-material';
import { ReactComponent as DownloadIcon } from 'assets/icons/download_icon.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete_icon.svg';
import { Card } from '../Card';
import { BillingMethod, CampaignStatus, Creative } from 'types';
import { getChipColor, getFormattedChfCurrency } from 'utils';
import { useIsMobile, useCampaignActionsByStatus } from 'hooks';
import { CreativePreview } from './CreativePreview';
import { BUTTON_HEIGHT_CONSTANT } from 'shared';
import { getLookerReportDownloadUrl } from 'utils/report';
import { CampaignStop } from 'components/CampaignStop';

type CampaignStats = {
  clicks: number;
  engagements: number;
  spentBudget: number;
};

type CampaignCardProps = {
  id: number;
  reportId?: string | null;
  title: string;
  dateDescription: string;
  description: string;
  status: CampaignStatus;
  onDelete: () => void;
  duplicate: {
    onDuplicate: () => void;
    isDuplicationLoading: boolean;
    duplicatingId?: number | null;
  };
  editPath: string;
  viewPath: string;
  firstCreative?: Creative | null;
  hasLowPerformance: boolean;
  invoicedAmount: number | null;
  isStopped?: boolean;
  budget?: number;
  billingMethod?: BillingMethod;
  stats?: CampaignStats | null;
};

const MobileDivider = () => <Divider sx={{ my: 4, width: '100%' }} orientation="horizontal" />;

export const CampaignCard = ({
  id,
  title,
  description,
  dateDescription,
  status,
  onDelete,
  duplicate,
  editPath,
  viewPath,
  hasLowPerformance,
  firstCreative,
  reportId,
  invoicedAmount,
  budget = 1000,
  billingMethod = BillingMethod.CLICK,
  stats,
}: CampaignCardProps) => {
  const { isMobile } = useIsMobile();
  const { onDuplicate, isDuplicationLoading, duplicatingId } = duplicate;
  const isDuplicating = isDuplicationLoading && duplicatingId === id;
  const {
    isOverviewAvailable,
    isEditAvailable,
    isDuplicateAvailable,
    isDeleteAvailable,
    isPdfReportAvailable,
    isStopAvailable,
  } = useCampaignActionsByStatus(status);

  const buttonStyles = (theme: Theme) => ({
    maxHeight: theme.spacing(BUTTON_HEIGHT_CONSTANT),
    fontSize: theme.spacing(3),
    '& .MuiButton-startIcon svg': {
      fontSize: theme.spacing(4),
      maxWidth: theme.spacing(4),
      maxHeight: theme.spacing(4),
    },
    '&.Mui-disabled': {
      color: 'grey.700',
    },
  });

  const { backgroundColor, color, borderColor } = getChipColor(status);

  return (
    <Card sx={{ border: 'none' }}>
      <Stack gap={isMobile ? 0 : 6} direction={isMobile ? 'column' : 'row'} alignItems="center">
        <CreativePreview creative={firstCreative} />
        {isMobile && <MobileDivider />}
        <Stack flex={2} gap={isMobile ? 1 : 0} width={1}>
          <Stack direction={'row'} gap={2.5} mb={isMobile ? 0 : 4}>
            <Chip
              label={status}
              sx={(theme) => ({
                width: 'fit-content',
                maxHeight: theme.spacing(6),
                fontWeight: 700,
                backgroundColor,
                color,
                border: `1px solid`,
                borderColor,
              })}
            />
            {hasLowPerformance && (
              <Chip
                label={'Zu tiefe Performance'}
                sx={(theme) => ({
                  width: 'fit-content',
                  backgroundColor: 'warning.light',
                  color: 'warning.dark',
                  maxHeight: theme.spacing(6),
                  fontWeight: 700,
                  border: '1px solid',
                  borderColor: 'warning.main',
                })}
              />
            )}
            <Typography>{dateDescription}</Typography>
          </Stack>
          <Typography variant="h4" mb={isMobile ? 0 : 1}>
            {title}
          </Typography>
          <Typography width="65%">{description}</Typography>
          {stats && (
            <Stack direction="row" gap={isMobile ? 2 : 4} mt={2} flexWrap="wrap">
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Budget
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {getFormattedChfCurrency(budget)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Ausgaben
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {getFormattedChfCurrency(stats.spentBudget)}
                </Typography>
              </Box>
              {billingMethod === BillingMethod.CLICK && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Klicks
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {stats.clicks.toLocaleString('de-CH')}
                  </Typography>
                </Box>
              )}
              {billingMethod === BillingMethod.ENGAGEMENT && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Engagements
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {stats.engagements.toLocaleString('de-CH')}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </Stack>
        {isMobile && <MobileDivider />}
        <Stack
          gap={2}
          alignItems={'flex-end'}
          justifyContent={isMobile ? 'space-between' : 'flex-end'}
          direction={isMobile ? 'row' : 'column'}
          flexWrap="wrap"
        >
          {isOverviewAvailable ? (
            <Button sx={buttonStyles} startIcon={<RemoveRedEyeOutlined />} component={Link} to={viewPath}>
              Details
            </Button>
          ) : null}
          {isEditAvailable && (
            <Button sx={buttonStyles} startIcon={<EditOutlined />} component={Link} to={editPath}>
              fertigstellen
            </Button>
          )}
          {isDuplicateAvailable ? (
            <Button
              sx={buttonStyles}
              startIcon={
                isDuplicating ? <CircularProgress sx={{ color: 'grey.700' }} size={20} /> : <ContentCopyOutlined />
              }
              data-testid={`delete-campaign-button`}
              onClick={onDuplicate}
              disabled={isDuplicating}
            >
              {isDuplicating ? 'wird dupliziert....' : 'Duplizieren'}
            </Button>
          ) : null}
          {isDeleteAvailable && (
            <Button
              sx={buttonStyles}
              startIcon={<DeleteIcon />}
              onClick={onDelete}
              data-testid={`delete-campaign-button-${id}`}
            >
              Löschen
            </Button>
          )}
          {reportId && isPdfReportAvailable ? (
            <Button
              sx={buttonStyles}
              startIcon={<DownloadIcon />}
              target="_blank"
              href={getLookerReportDownloadUrl(id)}
            >
              reporting als PDF
            </Button>
          ) : null}
          {isStopAvailable ? (
            <CampaignStop
              buttonStyles={buttonStyles as SxProps<Theme>}
              status={status}
              invoicedAmount={invoicedAmount}
              campaignId={id}
            />
          ) : null}
        </Stack>
      </Stack>
    </Card>
  );
};
